import { X } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../cn.js';
import { useUiLabels } from '../labels.js';
import { IconButton } from './Button.js';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const widths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl' };

/** Accessible modal built on the native <dialog> (focus trap, Esc and backdrop for free). */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  className,
}: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const labels = useUiLabels();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  // Rendered at the end of <body>: a dialog opened from inside a form (e.g. the message composer)
  // must not end up nested in it.
  return createPortal(
    <dialog
      ref={ref}
      onClose={onClose}
      onClick={(event) => {
        // Clicking the backdrop (the dialog element itself) closes it.
        if (event.target === ref.current) onClose();
      }}
      className={cn(
        'm-auto w-[calc(100%-2rem)] overflow-hidden rounded-2xl border border-border bg-surface p-0 text-text shadow-float open:animate-fade-in',
        widths[size],
        className,
      )}
    >
      {open && (
        <div className="flex max-h-[85vh] flex-col">
          <header className="flex items-start justify-between gap-4 px-5 pt-5 pb-3">
            <div>
              <h2 className="text-[17px] font-semibold tracking-tight">{title}</h2>
              {description && <p className="mt-1 text-sm text-muted">{description}</p>}
            </div>
            <IconButton label={labels.close} size="sm" onClick={onClose}>
              <X />
            </IconButton>
          </header>
          <div className="overflow-y-auto px-5 pb-5">{children}</div>
          {footer && (
            <footer className="flex justify-end gap-2 border-t border-border bg-surface-2/60 px-5 py-3">
              {footer}
            </footer>
          )}
        </div>
      )}
    </dialog>,
    document.body,
  );
}
